/* eslint-disable */
/* eslint-disable no-use-before-define */
// hooks/travelPlan/useTravelPlans.js
import { useCallback, useMemo } from 'react';
import { useTravelPlansContext } from './context';
import { validateTravelPlan } from './validateTravelPlan';
import { ACTIONS } from './reducer';
import Swal from 'sweetalert2';
import { apiCaller, APIError } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import { logger } from 'utils/environment/log';

export const useTravelPlans = () => {
    const [state, dispatch] = useTravelPlansContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const validatePlan = useCallback(async (planData) => {
        const { isValid, errors } = validateTravelPlan(planData);
        if (!isValid) {
            throw new APIError('Validation failed', 422, errors);
        }
        return { isValid, errors };
    }, []);

    const fetchPlans = useCallback(
        async (filter = {}, pagination = { page: 0, size: 20 }) => {
            try {
                setLoading(true);

                const searchCriteria = [];

                if (filter.dateRange) {
                    if (filter.dateRange.departureTime) {
                        searchCriteria.push({
                            key: 'departureTime',
                            value: filter.dateRange.departureTime,
                            operation: 'GREATER_THAN',
                            orPredicate: false
                        });
                    }

                    if (filter.dateRange.returnTime) {
                        searchCriteria.push({
                            key: 'returnTime',
                            value: filter.dateRange.returnTime,
                            operation: 'LESS_THAN',
                            orPredicate: false
                        });
                    }
                }

                Object.entries(filter)
                    .filter(([key, value]) => key !== 'dateRange' && value !== null && value !== undefined && value !== '')
                    .forEach(([key, value]) => {
                        searchCriteria.push({
                            key,
                            value,
                            operation: 'EQUAL',
                            orPredicate: false
                        });
                    });

                const pageable = {
                    page: pagination.page,
                    size: pagination.size
                };

                const response = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_PLANS}/q`, searchCriteria, { params: pageable });

                dispatch({ type: ACTIONS.SET_PLANS, payload: response });
                return response;
            } catch (error) {
                logger.error('Error in fetchPlans:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const searchPlans = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            try {
                const filter = {
                    dateRange: {
                        departureTime: searchParams.departureTime,
                        returnTime: searchParams.returnTime
                    },
                    // Add type filter if it exists
                    ...(searchParams.type && { type: searchParams.type })
                };

                const pagination = {
                    page: pageParams.page || 0,
                    size: pageParams.size || 20
                };

                const sort = pageParams.sort || ['departureTime,DESC'];

                return await fetchPlans(filter, pagination, sort);
            } catch (error) {
                logger.error('Error in searchPlans:', error);
                handleError(error);
                throw error;
            }
        },
        [dispatch, fetchPlans, handleError]
    );

    const createPlan = useCallback(
        async (planData) => {
            try {
                setLoading(true);
                const data = await apiCaller.postFormDatai(API_ENDPOINTS.TRAVEL_PLANS, planData, {});
                dispatch({ type: ACTIONS.ADD_PLAN, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updatePlan = useCallback(
        async (id, planData) => {
            try {
                setLoading(true);
                await validatePlan(planData);

                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${id}`, planData);
                dispatch({ type: ACTIONS.UPDATE_PLAN, payload: { id, data } });

                showSuccessMessage('Travel plan updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch, validatePlan]
    );

    const deletePlan = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.TRAVEL_PLANS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_PLAN, payload: id });

                showSuccessMessage('Travel plan deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getPlanById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_PLANS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_PLAN, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const updatePlanStatus = useCallback(
        async (planId, status) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/status`, { status });
                dispatch({
                    type: ACTIONS.SET_PLAN_STATUS,
                    payload: { id: planId, status }
                });
                showSuccessMessage(`Plan status updated to ${status}`);
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const addComment = useCallback(
        async (planId, comment) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/comments`, comment);
                dispatch({
                    type: ACTIONS.ADD_PLAN_COMMENT,
                    payload: data
                });
                showSuccessMessage('Comment added successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateApprover = useCallback(
        async (planId, approverData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(
                    `${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/approvers/${approverData.approverId}`,
                    approverData
                );
                dispatch({
                    type: ACTIONS.UPDATE_PLAN_APPROVER,
                    payload: data
                });
                showSuccessMessage('Approver updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateAttachments = useCallback(
        async (planId, files) => {
            try {
                setLoading(true);
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));

                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/attachments`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                dispatch({
                    type: ACTIONS.UPDATE_ATTACHMENTS,
                    payload: data
                });
                showSuccessMessage('Attachments updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateItinerary = useCallback(
        async (planId, itineraryData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/itinerary`, itineraryData);
                dispatch({
                    type: ACTIONS.UPDATE_ITINERARY,
                    payload: data
                });
                showSuccessMessage('Itinerary updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateBudget = useCallback(
        async (planId, budgetData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/budget`, budgetData);
                dispatch({
                    type: ACTIONS.UPDATE_BUDGET,
                    payload: data
                });
                showSuccessMessage('Budget updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateParticipants = useCallback(
        async (planId, participantsData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/participants`, participantsData);
                dispatch({
                    type: ACTIONS.UPDATE_PARTICIPANTS,
                    payload: data
                });
                showSuccessMessage('Participants updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const getTravelPlans = useCallback(
        async ({
            planName = '',
            departureTime = null,
            returnTime = null,
            type = '',
            venue = '',
            page = 0,
            size = 999999,
            sort = []
        } = {}) => {
            try {
                setLoading(true);
                const params = {
                    planName,
                    departureTime,
                    returnTime,
                    type,
                    venue,
                    page,
                    size,
                    ...(sort.length > 0 && { sort })
                };

                // Remove undefined and null parameters
                Object.keys(params).forEach(key => 
                    (params[key] === undefined || params[key] === null) && delete params[key]
                );

                const response = await apiCaller.get(API_ENDPOINTS.TRAVEL_PLANS, { 
                    params,
                    headers: {
                        'accept': '*/*'
                    }
                });
                dispatch({ type: ACTIONS.SET_PLANS, payload: response });
                return response;
            } catch (error) {
                logger.error('Error in getTravelPlans:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const getTravelPlanRequests = useCallback(
        async (planId) => {
            try {
                setLoading(true);
                const response = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/requests`, {
                    headers: {
                        'accept': '*/*'
                    }
                });
                return response;
            } catch (error) {
                logger.error('Error in getTravelPlanRequests:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError]
    );

    const getTravelPlanParticipants = useCallback(
        async (planId) => {
            try {
                setLoading(true);
                const response = await apiCaller.get(`${API_ENDPOINTS.TRAVEL_PLANS}/${planId}/requests`, {
                    headers: {
                        'accept': '*/*'
                    }
                });
                return response;
            } catch (error) {
                logger.error('Error in getTravelPlanParticipants:', error);
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError]
    );

    return useMemo(
        () => ({
            // State
            plans: state.plans,
            selectedPlan: state.selectedPlan,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            fetchPlans,
            searchPlans,
            getTravelPlans,
            getTravelPlanRequests,
            getTravelPlanParticipants,

            // CRUD Operations
            createPlan,
            updatePlan,
            deletePlan,
            getPlanById,

            // Status and Workflow
            updatePlanStatus,
            addComment,
            updateApprover,

            // Attachments and Documents
            updateAttachments,

            // Plan Details
            updateItinerary,
            updateBudget,
            updateParticipants,

            // Utilities
            clearError,
            validatePlan
        }),
        [
            state,
            fetchPlans,
            searchPlans,
            getTravelPlans,
            getTravelPlanRequests,
            getTravelPlanParticipants,
            createPlan,
            updatePlan,
            deletePlan,
            getPlanById,
            updatePlanStatus,
            addComment,
            updateApprover,
            updateAttachments,
            updateItinerary,
            updateBudget,
            updateParticipants,
            clearError,
            validatePlan
        ]
    );
};

export default useTravelPlans;
